import { WorkspacePlan } from '@up2stay/up2promo-api-client';
import { Check, Lock } from 'lucide-react';
import { cn } from '@up2stay/up2promo-ui';

import {
  PLAN_ORDER,
  planIncludesFeature,
  type FeatureKey,
} from '@/core/authz/entitlement-catalog';
import { useDictionary } from '@/core/providers/dictionary-provider';

type Cell = { kind: 'yes' } | { kind: 'no' } | { kind: 'text'; value: string };

function yes(): Cell {
  return { kind: 'yes' };
}
function no(): Cell {
  return { kind: 'no' };
}
function text(value: string): Cell {
  return { kind: 'text', value };
}

/** ✓ from the feature's minimum plan upwards — same source as upgrade CTAs. */
function featureCells(feature: FeatureKey): Cell[] {
  return PLAN_ORDER.map((plan) =>
    planIncludesFeature(plan, feature) ? yes() : no(),
  );
}

/**
 * Desktop feature comparison table. The recommended plan's column is
 * highlighted (Plus by default, Teams for Teams-only upsells, DEV-575).
 */
export function BillingFeatureComparison({
  highlightPlan = WorkspacePlan.PLUS,
}: {
  highlightPlan?: WorkspacePlan;
}) {
  const highlightIndex = PLAN_ORDER.indexOf(highlightPlan);
  const plansPage = useDictionary().workspace.billing.plansPage;
  const c = plansPage.comparison;
  const plans = [
    plansPage.free.name,
    plansPage.plus.name,
    plansPage.teams.name,
    plansPage.enterprise.name,
  ] as const;

  const rows: { label: string; cells: Cell[] }[] = [
    {
      label: c.users,
      cells: [
        text(c.usersFree),
        text(c.usersPlus),
        text(c.usersTeams),
        text(c.usersEnt),
      ],
    },
    { label: c.dashboard, cells: [yes(), yes(), yes(), yes()] },
    { label: c.leadEvents, cells: featureCells('feature.lead_events') },
    { label: c.products, cells: [yes(), yes(), yes(), yes()] },
    { label: c.branding, cells: featureCells('feature.profile_branding') },
    { label: c.integrations, cells: featureCells('feature.integrations') },
    { label: c.reputation, cells: featureCells('feature.reputation') },
    { label: c.feedback, cells: featureCells('feature.feedback') },
    { label: c.reputationAi, cells: featureCells('feature.reputation_ai') },
    {
      label: c.competitorBenchmark,
      cells: featureCells('feature.competitor_benchmark'),
    },
    // Locations themselves are never billed (Free needs them for stands);
    // only reputation tracking beyond the included count is (DEV-522).
    {
      label: c.locations,
      cells: [
        text(c.locationsAll),
        text(c.locationsAll),
        text(c.locationsAll),
        text(c.locationsAll),
      ],
    },
    {
      label: c.repLocations,
      cells: [
        text(c.repFree),
        text(c.repPlus),
        text(c.repTeams),
        text(c.repEnt),
      ],
    },
    {
      label: c.extraLocations,
      cells: [
        text(c.extraFree),
        text(c.extraPaid),
        text(c.extraPaid),
        text(c.extraEnt),
      ],
    },
    { label: c.roles, cells: featureCells('feature.custom_roles') },
    { label: c.sso, cells: featureCells('feature.sso') },
    { label: c.support, cells: [no(), no(), no(), yes()] },
  ];

  return (
    <section
      aria-label={plansPage.compareTitle}
      className="flex flex-col rounded-[18px] border border-border bg-card p-2 pb-3"
    >
      <h2 className="m-0 px-4 pb-2 pt-4 text-lg font-semibold tracking-[-0.02em] text-foreground">
        {plansPage.compareTitle}
      </h2>
      <div
        role="table"
        aria-label={plansPage.compareTitle}
        className="flex flex-col text-sm"
      >
        <div
          role="row"
          className="grid grid-cols-[minmax(0,1.6fr)_repeat(4,minmax(0,1fr))]"
        >
          <span
            role="columnheader"
            className="px-4 py-3.5 text-left text-[13px] font-medium text-muted-foreground"
          >
            {plansPage.featureColumn}
          </span>
          {plans.map((name, index) => (
            <span
              key={name}
              role="columnheader"
              className={cn(
                'px-4 py-3.5 text-center text-[13px] font-semibold',
                index === highlightIndex &&
                  'rounded-t-xl bg-[color:var(--ghost-accent-bg)] text-primary dark:bg-primary/15',
              )}
            >
              {name}
            </span>
          ))}
        </div>
        {rows.map((row) => (
          <div
            key={row.label}
            role="row"
            className="grid grid-cols-[minmax(0,1.6fr)_repeat(4,minmax(0,1fr))]"
          >
            <span
              role="rowheader"
              className="border-t border-border/70 px-4 py-3 text-muted-foreground"
            >
              {row.label}
            </span>
            {row.cells.map((cell, index) => (
              <span
                key={`${row.label}-${index}`}
                role="cell"
                className={cn(
                  'flex items-center justify-center border-t border-border/70 px-4 py-3 text-[13px]',
                  index === highlightIndex &&
                    'bg-[color:var(--ghost-accent-bg)] dark:bg-primary/10',
                )}
              >
                <ComparisonCell
                  cell={cell}
                  yesAria={plansPage.yesAria}
                  noAria={plansPage.noAria}
                />
              </span>
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}

function ComparisonCell({
  cell,
  yesAria,
  noAria,
}: {
  cell: Cell;
  yesAria: string;
  noAria: string;
}) {
  if (cell.kind === 'yes') {
    return (
      <span className="inline-flex text-primary" aria-label={yesAria}>
        <Check className="size-[18px]" aria-hidden />
      </span>
    );
  }
  if (cell.kind === 'no') {
    return (
      <span className="text-muted-foreground" aria-label={noAria}>
        –
      </span>
    );
  }
  return <span>{cell.value}</span>;
}

export function BillingPlanFeatureItem({
  label,
  locked,
}: {
  label: string;
  locked?: boolean;
}) {
  return (
    <li
      className={cn(
        'flex gap-2.5 text-[13px] leading-snug',
        locked ? 'text-muted-foreground' : 'text-foreground',
      )}
    >
      {locked ? (
        <Lock className="mt-0.5 size-[15px] shrink-0" aria-hidden />
      ) : (
        <Check className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden />
      )}
      <span>{label}</span>
    </li>
  );
}
